/**
 * Responsibility: Implement character creation behavior for the engine layer.
 * Scope: Owns engine-layer concerns only; cross-layer orchestration is excluded.
 */
import { normalizeSex } from './state'

export type CreationSex = 'male' | 'female'

function hashSeed(value: string): number {
  let hash = 2166136261
  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i)
    hash = Math.imul(hash, 16777619)
  }
  return hash >>> 0
}

export function resolveCreationSex(
  requested: unknown,
  generated: unknown,
  seedBasis: string,
): CreationSex {
  const direct = normalizeSex(requested) || normalizeSex(generated)
  if (direct === 'male' || direct === 'female') return direct
  return (hashSeed(seedBasis || 'adventure-machine') % 2 === 0) ? 'female' : 'male'
}

export function normalizeGenesisIdentity(raw: unknown, fallback: string): string {
  const text = String(raw ?? '').trim().replace(/\s+/g, ' ')
  if (!text) return String(fallback ?? '').trim()
  return text
}

export function buildPortraitPrompt(definition: {
  name?: string
  identity: string
  sex?: string
  bio?: string
}): string {
  const name = String(definition.name ?? '').trim()
  const identity = String(definition.identity ?? '').trim() || 'the character'
  const sex = normalizeSex(definition.sex)
  const bio = String(definition.bio ?? '').trim() || 'A fantasy adventurer with a grounded, coherent appearance.'
  const subject = [name, identity].filter(Boolean).join(' — ') || identity
  const sexClause = sex ? ` Sex: ${sex}.` : ''
  return `Single-character fantasy portrait of ${subject}.${sexClause} Canon character definition: ${bio}. Strictly preserve identity cues from the definition, and do not contradict the stated sex. Head-and-shoulders framing, no extra people, no text, cinematic detail.`
}
